import { DownloadOutlined, SaveOutlined } from '@ant-design/icons';
import { useMutation, useQuery as useReactQuery } from '@tanstack/react-query';
import { App, Badge, Button, Form, Spin, Tabs, Tooltip } from 'antd';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useNavigate } from 'react-router-dom';
import { setCompanies } from '../../../contextRedux/Company/CompanyStore';
import { setDevices } from '../../../contextRedux/DeviceType/DeviceTypeStore';
import { setLaborExtras } from '../../../contextRedux/LaborExtra/LaborExtraStore';
import {
  setPmAdditional,
  updateEquipmentTab,
  updateField,
} from '../../../contextRedux/PMAdditionalAct/PmAdditionalActModel';
import { setPmContractStatuses } from '../../../contextRedux/PmContractStatus/PmContractStatusStore';
import { setPmOfferContractTypes } from '../../../contextRedux/PmOfferContractTypes/PmOfferContractTypesStore';
import { setSystemTypes } from '../../../contextRedux/SystemType/SystemTypeStore';
import { setUnitMeasures } from '../../../contextRedux/UnitMeasure/UnitMeasureStore';
import { setUsers } from '../../../contextRedux/User/UserStore';
import CompanyService from '../../../services/company/CompanyService';
import ConfigurationService from '../../../services/configuration/ConfigurationService';
import DeviceTypeService from '../../../services/deviceType/DeviceTypeService';
import LaborExtraService from '../../../services/laborExtra/LaborExtraService';
import PmContractService from '../../../services/pmContract/PmContractService';
import PmContractAdditionalActService from '../../../services/pmContractAdditionalAct/ContractAdditionalActService';
import SystemTypeService from '../../../services/systemType/SystemTypeService';
import UserService from '../../../services/user/UserService';
import WorkPlaceService from '../../../services/workPlace/WorkPlaceService';
import { RootState } from '../../../store';
import { saveBlobCrossPlatform } from '../../../utils/saveBlobCrossPlatform';
import { Tab } from '../../../utils/tabs';
import AddEquipmentTab from './Tabs/AddEquipmentTab/AddEquipmentTab';
import CommentsTab from './Tabs/CommentsTab/CommentsTab';
import CompanyDetailsTab from './Tabs/CompanyDetailsTab/CompanyDetailsTab';
import LaborTab from './Tabs/LaborTab/LaborTab';
import ReviewTab from './Tabs/ReviewTab/ReviewTab';
import SummaryTab from './Tabs/SummaryTab/SummaryTab';

const NewPmAdditionalAct: FC = () => {
  const isMobile = useMediaQuery({ maxWidth: 720 });
  // const isTab = useMediaQuery({ maxWidth: 920});
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const useQuery = () => new URLSearchParams(useLocation().search);
  const query = useQuery();
  const [form] = Form.useForm();

  const [id, setId] = useState<string | null>(query.get('id'));

  const { modal, message } = App.useApp();
  const reduxpPAdditionalAct = useSelector((state: RootState) => state?.models.pmAdditionalAct);
  const addedDevices = useSelector((state: RootState) => state.models.pmAdditionalAct.pmContractWorkPlaceItems);
  const companies = useSelector((state: RootState) => state.store.company.data);
  const [version, setVersion] = useState<number>();
  const [fileList, setFileList] = useState<any>([]);

  let totalOfferValue =
    Number.parseFloat(
      (
        reduxpPAdditionalAct.pmContractWorkPlaceItems.reduce((total, item) => total + item.laborPrice, 0) +
        (reduxpPAdditionalAct.pmContractLaborExtras
          ? reduxpPAdditionalAct.pmContractLaborExtras.reduce(
              (total: any, item: any) => total + item?.piece * item?.price,
              0
            )
          : 0)
      ).toFixed(2)
    ) +
    Number.parseFloat(
      reduxpPAdditionalAct.pmContractWorkPlaceItems.reduce((total, item) => {
        const totalPrice = item.price;
        return total + totalPrice;
      }, 0)
    );
  const userRols = 'sales';

  const isShowCompanyDetailsTab: boolean = !!(
    reduxpPAdditionalAct.companyId &&
    reduxpPAdditionalAct.accountManagerId &&
    reduxpPAdditionalAct.accountSaleId &&
    reduxpPAdditionalAct.enumPmOfferContractTypeId &&
    reduxpPAdditionalAct.enumPmContractStatusId &&
    reduxpPAdditionalAct.date &&
    reduxpPAdditionalAct.dateStart &&
    reduxpPAdditionalAct.dateEnd &&
    reduxpPAdditionalAct.registrationNumber
  );

  const { data: _accountSales, isFetching: isUsersLoading } = useReactQuery({
    queryKey: ['users'],
    queryFn: () =>
      UserService.search({
        isActive: true,
        rols: userRols,
      }).then((data) => {
        dispatch(setUsers(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    const idFromUrl = query.get('id');
    if (!idFromUrl) {
    }
    setId(idFromUrl);
    dispatch(updateField({ field: 'id', value: idFromUrl }));
  }, [location.search]);

  const { data: _pmContract, isFetching: isPmContractLoading } = useReactQuery({
    queryKey: ['pmContracts'],
    queryFn: () =>
      PmContractAdditionalActService.search({
        pmContractId: id,
        isMetadataIncluded: true,
      }).then((data: any) => {
        dispatch(setPmAdditional(data?.response[0]));
        return data?.response[0];
      }),
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _status, isFetching: isStatusLoading } = useReactQuery({
    queryKey: ['pmContractStatuses'],
    queryFn: () =>
      ConfigurationService.searchPmContractStatus({}).then((data) => {
        dispatch(setPmContractStatuses(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _contractTypes, isFetching: isPmOfferTypesLoading } = useReactQuery({
    queryKey: ['pmOfferContractTypes'],
    queryFn: () =>
      ConfigurationService.searchPmOfferContractType({}).then((data) => {
        dispatch(setPmOfferContractTypes(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _contract, isFetching: isPmContractsLoading } = useReactQuery({
    queryKey: ['contractAdditionalActs'],
    queryFn: () =>
      PmContractAdditionalActService.search({
        pmContractId: id,
        isMetadataIncluded: true,
      }).then((data) => {
        dispatch(setPmAdditional(data?.response[0]));
        setVersion(data?.version);
        return data?.response[0];
      }),
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _companies, isFetching: isCompaniesLoading } = useReactQuery({
    queryKey: ['companies'],
    queryFn: () =>
      CompanyService.search({
        isAgentIncluded: true,
        isWorkPlacesIncluded: true,
        isAccountManagerIncluded: true,
        isActive: true,
      }).then((data) => {
        dispatch(setCompanies(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _unitMeasures, isFetching: isUnitMeasuresLoading } = useReactQuery({
    queryKey: ['unitMeasures'],
    queryFn: () =>
      ConfigurationService.searchConfiguration({ tag: 'unitMeasure' }).then((data) => {
        dispatch(setUnitMeasures(data?.response?.section));

        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _laborExtras, isFetching: isLaborExtrasLoading } = useReactQuery({
    queryKey: ['laborExtras'],
    queryFn: () =>
      LaborExtraService.search({ isActive: true }).then((data) => {
        dispatch(setLaborExtras(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _systemTypes, isFetching: isSystemTypesLoading } = useReactQuery({
    queryKey: ['systemTypes'],
    queryFn: () =>
      SystemTypeService.search({
        isDeviceTypeIncluded: true,
        isActive: true,
      }).then((data) => {
        dispatch(setSystemTypes(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _deviceTypes, isFetching: isDeviceTypesLoading } = useReactQuery({
    queryKey: ['deviceTypes'],
    queryFn: () =>
      DeviceTypeService.search({
        isSystemTypeIncluded: true,
        isLaborIncluded: true,
        isActive: true,
      }).then((data) => {
        dispatch(setDevices(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const {
    data: _pmContractComments,
    refetch: searchComments,
    isFetching: isPmContractCommentsLoading,
  } = useReactQuery({
    queryKey: ['pmContractComment'],
    queryFn: () =>
      PmContractAdditionalActService.searchComment({
        ids: [id!],
        isMetadataIncluded: true,
      }).then((data) => {
        const departments = ['general', 'contabilitate', 'manager', 'instalatori'];

        // Initialize offerComments with all departments
        const initialContractComments = departments.map((departmentName) => {
          const existingComment = data?.response?.find((comment: any) => comment.departmentName === departmentName);
          return (
            existingComment || {
              id: '',
              pmContractId: id,
              departmentName,
              message: '',
              pmContractCommentFiles: [],
            }
          );
        });
        dispatch(updateField({ field: 'pmContractCommentsTab', value: initialContractComments }));
        return data?.response;
      }),
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { mutate: saveAdditionalActPmContract, isPending: isSavingAdditionalActPmContract } = useMutation({
    mutationFn: (formData: any) =>
      PmContractAdditionalActService.upsert(formData)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          if (data) {
            handleResponse(data);
            dispatch(setPmAdditional(data?.response));
            setFileList([]);
            data?.response &&
              navigate(`/project-management/pmContract/pmact-additionally/edit?id=${data?.response?.id}`);
            if (!!id) {
              searchComments();
            }
          } else {
            console.error('Received data is not in JSON format or JSON parsing failed.');
          }
        })
        .catch((error) => {
          console.error('Error processing your request:', error);
        }),
  });

  const onSaveAdditionalAct = () => {
    if (!fileList[0]) {
      message.error('Incarca fisiere!');
      return;
    }

    const formData = new FormData();

    // Append form fields
    formData.append('Id', reduxpPAdditionalAct.id);
    formData.append('CompanyId', reduxpPAdditionalAct.companyId);
    formData.append('Number', reduxpPAdditionalAct.number);
    formData.append('Remarks', reduxpPAdditionalAct.remarks);
    formData.append('Date', reduxpPAdditionalAct.date);
    formData.append('DateStart', reduxpPAdditionalAct.dateStart);
    formData.append('DateEnd', reduxpPAdditionalAct.dateEnd);
    //formData.append('DatePeriod', reduxPmContract.datePeriod);
    //formData.append('AcquisitionType', reduxPmContract.acquisitionType);
    //formData.append('IsExpedite', reduxPmContract.isExpedite);
    //formData.append('Intervention', reduxPmContract.intervention);
    formData.append('OfferId', reduxpPAdditionalAct.pmOfferId);
    formData.append('Status', reduxpPAdditionalAct.enumPmContractStatus);
    formData.append('EnumContractStatusId', reduxpPAdditionalAct.enumPmContractStatusId);

    // Append nested JSON as a string
    formData.append('PmContractWorkPlaceItems', JSON.stringify(reduxpPAdditionalAct.pmContractWorkPlaceItems));
    formData.append('PmContractLaborExtras', JSON.stringify(reduxpPAdditionalAct.pmContractLaborExtras));
    formData.append('PmContractIntervals', JSON.stringify(form?.getFieldValue('pmContractIntervals')));
    formData.append('PmContractComments', JSON.stringify(reduxpPAdditionalAct.pmContractCommentsTab));

    formData.append('Files', fileList[0]);
    saveAdditionalActPmContract(formData);
  };

  const { mutate: updateWorkPace } = useMutation({
    mutationFn: (formData: any) => WorkPlaceService.upsert(formData.id, formData),
    onSuccess: () => {},
    onError: () => {
      message.error('Eroare procesare request.');
    },
  });

  const filterAndGroupData = (data: any) => {
    const result: any = {};

    data.forEach((item: any) => {
      const { workPlace, workPlaceId, systemTypeId, deviceTypeId, piece, isAnotherProvider, details } = item;

      if (!result[workPlaceId]) {
        result[workPlaceId] = {
          workPlace: workPlace,
          workPlaceId: workPlaceId,
          devices: [],
        };
      }

      result[workPlaceId].devices.push({
        systemTypeId,
        deviceTypeId,
        piece,
        isAnotherProvider,
        details,
      });
    });

    return Object.values(result);
  };

  const handleSaveContract = async () => {
    if (!fileList[0]) {
      message.error('Incarca fisiere!');
      return;
    }
    try {
      modal.confirm({
        title: 'Confirm Update',
        content: 'Sigur doriți să actualizați punctele de lucru cu configurația curentă?',
        okText: 'Da',
        cancelText: 'Nu',
        onOk: async () => {
          const workPlaceItems = filterAndGroupData(reduxpPAdditionalAct.pmContractWorkPlaceItems);
          workPlaceItems.forEach((workPlaceItem: any) => {
            const formData = {
              ...workPlaceItem?.workPlace,
              agents:
                companies
                  .find((c: any) => c.id == workPlaceItem?.workPlace?.companyId)
                  .workPlaces.find((wp: any) => wp.id == workPlaceItem.workPlace.id)?.agents ?? [],
              contactPersons:
                companies
                  .find((c: any) => c.id == workPlaceItem?.workPlace?.companyId)
                  .workPlaces.find((wp: any) => wp.id == workPlaceItem?.workPlace?.id)?.contactPersons ?? [],
              workPlaceItems: workPlaceItem.devices.map((d: any) => {
                return {
                  id: '',
                  workPlaceId: workPlaceItem.workPlaceId,
                  deviceTypeId: d.deviceTypeId,
                  systemTypeId: d.systemTypeId,
                  details: d.details ?? '',
                  piece: d.piece,
                  metadata: '{}',
                  isAnotherProvider: d.isAnotherProvider ?? false,
                };
              }),
            };
            updateWorkPace(formData);
          });
          await onSaveAdditionalAct();
        },
        onCancel: async () => {
          await onSaveAdditionalAct();
        },
      });
    } catch (errorInfo: any) {
      message.error(errorInfo?.errorFields[0]?.errors);
    }
  };

  const handleResponse = (data: any) => {
    if (data?.errors?.length > 0) {
      data?.errors?.forEach((item: any) => {
        message.error(item);
      });
    } else {
      message.success(
        <span>
          <span className='font-weight-bold'>{data?.response?.number}</span> a fost {!!id ? 'modificat' : 'adaugat'} cu
          success
        </span>
      );
    }
  };

  const { mutate: viewPdfPmAdditionalAct, isPending: isViewingPdfAdditionalAct } = useMutation({
    mutationFn: (formData: any) => PmContractService.preview(id, formData),
    onSuccess: (data: any) => {
      if (!data || !data.blob) {
        throw new Error('Invalid response received from the server');
      }
      return data.blob().then((blob: any) => {
        const url = window.URL.createObjectURL(blob);
        dispatch(
          updateEquipmentTab({
            field: 'pdfUrl',
            value: url,
          })
        );
      });
    },
    onError: () => {
      message.error('Eroare procesare request.');
    },
  });

  const viewPdf = () => {
    if (isShowCompanyDetailsTab) {
      viewPdfPmAdditionalAct(reduxpPAdditionalAct);
    }
  };

  const { mutate: downloadAdditionalFile, isPending: isDownloadingPmAdditionalActFile } = useMutation({
    mutationFn: () =>
      PmContractService.downloadAdditionalFile(id).then((response) => {
        // Extract filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'file.zip'; // Default filename if header is missing

        if (contentDisposition) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(contentDisposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, '');
          }
        }

        return response
          .blob()
          .then((blob: any) => {
            saveBlobCrossPlatform(blob, filename);
          })
          .catch((error: any) => {
            console.error('Error processing your request:', error);
          });
      }),
  });

  const tabs: (Tab | false)[] = [
    {
      key: 'companyTab',
      label: 'Detalii Client',
      children: <CompanyDetailsTab form={form} fileList={fileList} setFileList={setFileList} />,
    },
    isShowCompanyDetailsTab && {
      key: 'deviceTab',
      label: 'Adaugare Echipament',
      children: <AddEquipmentTab />,
    },
    addedDevices?.length > 0 && {
      key: 'summaryTab',
      label: (
        <>
          Vizualizare Echipamente Adaugate
          <Badge
            color='blue'
            style={{ marginLeft: '5px' }}
            count={addedDevices.length > 0 ? addedDevices.length : ''}
          ></Badge>
        </>
      ),
      children: <SummaryTab />,
    },
    addedDevices.length > 0 && {
      key: 'manoperaTab',
      label: 'Manopera',
      children: <LaborTab></LaborTab>,
    },
    addedDevices?.length! > 0 && {
      key: 'commentsTab',
      label: 'Observatii si Comentarii',
      children: <CommentsTab />,
    },
    addedDevices?.length! > 0 && {
      key: 'reviewTab',
      label: 'Review',
      children: <ReviewTab isViewingPdfPmAdditionalAct={isViewingPdfAdditionalAct} />, // Use key to force re-render
    },
  ];

  const filteredTabs: Tab[] = tabs.filter((tab): tab is Tab => tab !== false);

  if (
    isSavingAdditionalActPmContract ||
    isDownloadingPmAdditionalActFile ||
    isPmContractLoading ||
    isCompaniesLoading ||
    isLaborExtrasLoading ||
    isStatusLoading ||
    isUnitMeasuresLoading ||
    isSystemTypesLoading ||
    isDeviceTypesLoading ||
    isPmOfferTypesLoading ||
    isPmContractCommentsLoading ||
    isPmContractsLoading ||
    isUsersLoading
  ) {
    return <Spin size='large' className='spinner' />;
  }

  return (
    <>
      <div className='topLine-pm'>
        <h2>
          {id
            ? `PM Act Aditional - V${version} - Nr. Contract ${reduxpPAdditionalAct?.number}`
            : 'PM Act Aditional Nou'}
        </h2>
        <div className='totalLine-pm'>
          <h3>{'Total - ' + totalOfferValue + ' lei'}</h3>
          {addedDevices?.some((item: any) => item.addition < 50)! && (
            <h2 style={{ color: 'red' }}>Adaosul produselor s-a modificat sub 50%</h2>
          )}
          <div className='buttons-pm'>
            <Button className='saveBtn-pm' onClick={() => handleSaveContract()}>
              {isMobile ? (
                <Tooltip title='Salveaza Act Aditional'>
                  <SaveOutlined />
                </Tooltip>
              ) : (
                'Salveaza Act Aditional'
              )}
            </Button>

            <Button className='download-pdf-pm' onClick={() => downloadAdditionalFile()}>
              {isMobile ? (
                <Tooltip title='Descărcare PDF'>
                  <DownloadOutlined />
                </Tooltip>
              ) : (
                <>
                  {' '}
                  <DownloadOutlined /> Descarca Acte Aditionale ZIP{' '}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
      <Tabs
        className='tabs'
        defaultActiveKey='1'
        items={filteredTabs}
        // tabPosition={isMobile || isTab ? 'top' : 'left'}
        tabBarStyle={{ backgroundColor: '#DADBDD' }}
        onChange={(key) => {
          if (key === 'reviewTab') {
            viewPdf();
          }
        }}
      />
    </>
  );
};

export default NewPmAdditionalAct;
